import { useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { RouterLinkAs } from '@/lib/router-link';
import { requirePlatformRole } from '@/lib/route-guards';
import {
  Body,
  Button,
  Card,
  Field,
  I,
  Input,
  PageHeader,
  RadioCard,
  RadioCardGroup,
  Row,
  Screen,
  ScreenBody,
  Stack,
  Textarea,
} from '@leanin/ui';
import { useCreateSpace } from '@/lib/mutations/spaces';
import { failureFromError } from '@/lib/api-error';

type Access = 'public' | 'unlisted' | 'private';
type JoinPolicy = 'open' | 'request' | 'invite_only';

export const Route = createFileRoute('/_authed/groups/new')({
  // Creating a group is admin-only (the server enforces it on POST
  // /api/v1/spaces). Redirect non-admins who reach the form by URL so they
  // never hit a 403 they can't act on.
  beforeLoad: ({ context }) => {
    requirePlatformRole(context, {
      role: 'admin',
      bounceTo: () => redirect({ to: '/groups' }),
    });
  },
  component: GroupCreatePage,
});

function GroupCreatePage() {
  const t = useTranslations('Spaces');
  const navigate = useNavigate();
  const createSpace = useCreateSpace();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [access, setAccess] = useState<Access>('public');
  const [joinPolicy, setJoinPolicy] = useState<JoinPolicy>('open');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const canSubmit = name.trim().length > 0;

  const onSubmit = async () => {
    setSubmitError(null);
    if (!canSubmit) {
      setSubmitError(t('new.validationName'));
      return;
    }
    let detail;
    try {
      detail = await createSpace.mutateAsync({
        name: name.trim(),
        access,
        joinPolicy,
        ...(description.trim().length > 0
          ? { description: description.trim() }
          : {}),
      });
    } catch (err) {
      setSubmitError(failureFromError(err, t('new.createFailed')).message);
      return;
    }
    navigate({ to: '/groups/$id', params: { id: detail.id } });
  };

  const isPending = createSpace.isPending;

  const actions = (
    <Row gap="sm">
      <Button href="/groups" linkAs={RouterLinkAs} variant="ghost">
        {t('new.cancel')}
      </Button>
      <Button
        variant="primary"
        iconRight={I.arrow}
        onClick={onSubmit}
        disabled={isPending || !canSubmit}
      >
        {isPending ? t('new.creating') : t('new.create')}
      </Button>
    </Row>
  );

  return (
    <Screen>
      <ScreenBody gap="xl">
        <PageHeader
          eyebrow={t('new.eyebrow')}
          title={t('new.title')}
          subtitle={t('new.subtitle')}
          back={{ label: t('new.back'), href: '/groups' }}
          actions={actions}
        />
        <Card pad="spacious">
          <Stack gap="lg">
            <Field required>
              <Field.Label>{t('new.nameLabel')}</Field.Label>
              <Field.Description>
                {t('new.nameDescription')}
              </Field.Description>
              <Field.Control>
                <Input
                  placeholder={t('new.namePlaceholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Field.Control>
            </Field>
            <Field>
              <Field.Label>{t('new.descriptionLabel')}</Field.Label>
              <Field.Description>
                {t('new.descriptionDescription')}
              </Field.Description>
              <Field.Control>
                <Textarea
                  rows={3}
                  placeholder={t('new.descriptionPlaceholder')}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field.Control>
            </Field>
            <Field required>
              <Field.Label>{t('new.accessLabel')}</Field.Label>
              <Field.Control>
                <RadioCardGroup
                  value={access}
                  onValueChange={(v) => setAccess(v as Access)}
                  columns={3}
                  aria-label={t('new.accessLabel')}
                >
                  <RadioCard value="public" title={t('new.accessPublic')} />
                  <RadioCard value="unlisted" title={t('new.accessUnlisted')} />
                  <RadioCard value="private" title={t('new.accessPrivate')} />
                </RadioCardGroup>
              </Field.Control>
            </Field>
            <Field required>
              <Field.Label>{t('new.joiningLabel')}</Field.Label>
              <Field.Control>
                <RadioCardGroup
                  value={joinPolicy}
                  onValueChange={(v) => setJoinPolicy(v as JoinPolicy)}
                  columns={3}
                  aria-label={t('new.joiningLabel')}
                >
                  <RadioCard value="open" title={t('new.joinOpen')} />
                  <RadioCard value="request" title={t('new.joinRequest')} />
                  <RadioCard value="invite_only" title={t('new.joinInvite')} />
                </RadioCardGroup>
              </Field.Control>
            </Field>

            {submitError !== null ? (
              <Card pad="default" tone="burgundy">
                <Body color="primary">{submitError}</Body>
              </Card>
            ) : null}

            <Row justify="flex-end">{actions}</Row>
          </Stack>
        </Card>
      </ScreenBody>
    </Screen>
  );
}
